import Component from '@glimmer/component';
import type { Token, Tokens } from 'marked';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import Button from 'tio-ui/components/button';
import {
  PhMagnifyingGlassPlus,
  PhMagnifyingGlassMinus,
  PhArrowsOut,
  PhArrowCounterClockwise,
  PhMouseSimple,
} from 'ember-phosphor-icons';
import { SvgPanZoomManager } from '../utils/svg-pan-zoom.ts';
import { escapeHtml } from '../utils/escape-html.ts';

interface MarkedRendererContext {
  parser: {
    parseInline: (tokens: Token[]) => string;
  };
}

interface MarkdownRendererSignature {
  Args: {
    /** Markdown content to render */
    content: string;
    /** Enable pan/zoom controls for mermaid diagrams (default: false) */
    enablePanZoom?: boolean;
    /** Fixed height for mermaid diagram containers in pixels (default: 600, only used with enablePanZoom) */
    diagramHeight?: number;
    /** Custom CSS classes for the prose container */
    class?: string;
  };
  Element: HTMLDivElement;
}

/**
 * Markdown renderer with optional mermaid diagram support and pan/zoom controls.
 *
 * Usage:
 *   Simple markdown (no pan/zoom):
 *   <MarkdownRenderer @content={{this.markdown}} />
 *
 *   With pan/zoom for diagrams:
 *   <MarkdownRenderer @content={{this.markdown}} @enablePanZoom={{true}} />
 *
 *   Custom diagram height:
 *   <MarkdownRenderer @content={{this.markdown}} @enablePanZoom={{true}} @diagramHeight={{500}} />
 */
export default class MarkdownRenderer extends Component<MarkdownRendererSignature> {
  @tracked scrollZoomEnabled = false;
  @tracked hasMermaidDiagrams = false;

  private panZoomManager = new SvgPanZoomManager();

  get enablePanZoom(): boolean {
    return this.args.enablePanZoom ?? false;
  }

  get diagramHeight(): number {
    return this.args.diagramHeight ?? 600;
  }

  /**
   * Check if content contains mermaid code blocks.
   * Used to conditionally show controls only when diagrams exist.
   */
  get contentHasMermaid(): boolean {
    const content = this.args.content;
    if (!content) return false;
    // Check for ```mermaid code blocks
    return /```mermaid/i.test(content);
  }

  toggleScrollZoom = () => {
    this.scrollZoomEnabled = !this.scrollZoomEnabled;
    this.panZoomManager.setScrollZoomEnabled(this.scrollZoomEnabled);
  };

  zoomIn = () => {
    this.panZoomManager.zoomInAll();
  };

  zoomOut = () => {
    this.panZoomManager.zoomOutAll();
  };

  resetView = () => {
    this.panZoomManager.resetAll();
  };

  fitDiagrams = () => {
    this.panZoomManager.fitAll();
  };

  sanitizeLinkHref = (href: string) => {
    const trimmed = href.trim();
    if (!trimmed) return null;

    const normalized = Array.from(trimmed)
      .filter((char) => {
        const code = char.charCodeAt(0);
        return code > 32 && code !== 127;
      })
      .join('')
      .toLowerCase();
    const scheme = normalized.match(/^([a-z][a-z\d+.-]*):/i)?.[1];

    if (scheme) {
      return ['http', 'https', 'mailto'].includes(scheme) ? trimmed : null;
    }

    return trimmed.startsWith('//') ? null : trimmed;
  };

  renderMarkdown = modifier((element: HTMLElement) => {
    this.render(element);

    // Cleanup on teardown
    return () => {
      this.panZoomManager.cleanup();
      this.scrollZoomEnabled = false;
    };
  });

  render = async (element: HTMLElement) => {
    const content = this.args.content;
    if (!content) {
      element.innerHTML = '';
      return;
    }

    // Cleanup previous state
    this.panZoomManager.cleanup();
    this.scrollZoomEnabled = false;
    this.hasMermaidDiagrams = false;

    try {
      const { marked } = await import('marked');
      const mermaid = (await import('mermaid')).default;

      const renderer = new marked.Renderer();
      let mermaidIdCounter = 0;
      const sanitizeLinkHref = this.sanitizeLinkHref;

      // Escape raw HTML in markdown to prevent XSS from untrusted content
      renderer.html = ({ text }: { text: string }) => {
        return escapeHtml(text);
      };

      renderer.link = function (this: MarkedRendererContext, { href, title, tokens }: Tokens.Link) {
        const text = this.parser.parseInline(tokens);
        const sanitizedHref = sanitizeLinkHref(href);
        if (!sanitizedHref) return text;

        const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
        return `<a href="${escapeHtml(sanitizedHref)}"${titleAttr}>${text}</a>`;
      };

      renderer.code = ({ text, lang }: { text: string; lang?: string }) => {
        if (lang === 'mermaid') {
          if (this.enablePanZoom) {
            // Pan/zoom enabled: Use resizable container with resize handle outside content area
            return `<div class="mermaid-container relative border tio-border-default rounded-lg my-4 tio-bg-surface resize-y overflow-hidden" data-mermaid-id="${mermaidIdCounter}" style="min-height: 200px;">
              <div class="mermaid-diagram w-full h-full" data-mermaid-id="${mermaidIdCounter++}">${escapeHtml(text)}</div>
              <div class="absolute bottom-2 right-2 text-xs tio-fg-disabled pointer-events-none">Drag to pan • Double-click to reset</div>
            </div>`;
          } else {
            // Simple mode: Basic centered mermaid rendering
            return `<div class="tio-flex-center mermaid p-4" data-mermaid-id="${mermaidIdCounter++}">${escapeHtml(text)}</div>`;
          }
        }
        return `<pre class="tio-bg-sunken p-3 rounded overflow-x-auto my-2"><code class="language-${lang || 'text'}">${escapeHtml(text)}</code></pre>`;
      };

      const html = await marked.parse(content, { renderer });
      element.innerHTML = html;

      mermaid.initialize({
        startOnLoad: false,
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
        securityLevel: this.enablePanZoom ? 'loose' : 'strict', // loose needed for SVG manipulation
      });

      // Find and render all mermaid diagrams
      const selector = this.enablePanZoom ? '.mermaid-diagram' : '.mermaid';
      const mermaidElements = element.querySelectorAll<HTMLDivElement>(selector);

      // Track whether we actually have diagrams to render
      this.hasMermaidDiagrams = mermaidElements.length > 0;

      for (const el of mermaidElements) {
        const source = el.textContent || '';
        const mermaidId = el.dataset.mermaidId || '0';
        const id = `mermaid-svg-${mermaidId}`;
        el.textContent = '';

        try {
          const { svg } = await mermaid.render(id, source);
          el.innerHTML = svg;

          // Initialize pan/zoom if enabled
          if (this.enablePanZoom) {
            const svgElement = el.querySelector('svg');
            if (svgElement) {
              // Get the parent container (the resizable one)
              const container = el.parentElement;

              // Set container height based on SVG's natural dimensions
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox && container) {
                const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number);
                if (vbWidth && vbHeight) {
                  // Calculate height based on container width and SVG aspect ratio
                  const containerWidth = container.clientWidth || 800;
                  const aspectRatio = vbHeight / vbWidth;
                  // Clamp height between 300px and 800px
                  const calculatedHeight = Math.min(
                    Math.max(containerWidth * aspectRatio, 300),
                    800
                  );
                  container.style.height = `${calculatedHeight}px`;
                }
              } else if (container) {
                // Fallback: use default height
                container.style.height = `${this.diagramHeight}px`;
              }

              this.panZoomManager.add(mermaidId, svgElement, {
                scrollZoomEnabled: this.scrollZoomEnabled,
              });
            }
          }
        } catch (e) {
          const error = e as Error;
          el.innerHTML = `<pre class="tio-fg-danger p-2 tio-bg-danger-soft rounded"><strong>Mermaid Parse Error:</strong><br>${escapeHtml(error.message)}</pre>`;
        }
      }
    } catch (error) {
      const err = error as Error;
      element.innerHTML = `<pre class="tio-fg-danger">${escapeHtml(content)}</pre>`;
      console.error('Markdown rendering error:', err);
    }
  };

  <template>
    {{#if this.enablePanZoom}}
      <div class="markdown-renderer-with-controls relative">
        {{! Diagram Controls Toolbar - Only show if content has mermaid blocks }}
        {{#if this.contentHasMermaid}}
          <div class="tio-row-between p-2 tio-surface-sunken sticky top-0 z-10 mb-3 shadow-sm">
            <div class="tio-row-extra-tight">
              <span class="text-sm tio-fg-secondary mr-2">Diagram:</span>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                title="Zoom out"
                {{on "click" this.zoomOut}}
              >
                <PhMagnifyingGlassMinus @size="18" />
              </Button>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                title="Zoom in"
                {{on "click" this.zoomIn}}
              >
                <PhMagnifyingGlassPlus @size="18" />
              </Button>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                title="Fit to view"
                {{on "click" this.fitDiagrams}}
              >
                <PhArrowsOut @size="18" />
              </Button>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                title="Reset view"
                {{on "click" this.resetView}}
              >
                <PhArrowCounterClockwise @size="18" />
              </Button>
              <div class="border-l tio-border-default h-5 mx-2"></div>
              <Button
                @appearance={{if this.scrollZoomEnabled "outlined" "minimal"}}
                @intent={{if this.scrollZoomEnabled "success" "primary"}}
                @size="sm"
                title={{if
                  this.scrollZoomEnabled
                  "Scroll zoom ON - click to disable"
                  "Scroll zoom OFF - click to enable"
                }}
                {{on "click" this.toggleScrollZoom}}
              >
                <PhMouseSimple @size="18" />
                <span class="ml-1 text-xs">{{if
                    this.scrollZoomEnabled
                    "Scroll Zoom ON"
                    "Scroll Zoom"
                  }}</span>
              </Button>
            </div>
          </div>
        {{/if}}

        <div
          {{this.renderMarkdown}}
          class="prose prose-sm dark:prose-invert max-w-none tio-prose-content {{@class}}"
          ...attributes
        ></div>
      </div>
    {{else}}
      <div
        {{this.renderMarkdown}}
        class="prose prose-sm dark:prose-invert max-w-none tio-prose-content {{@class}}"
        ...attributes
      ></div>
    {{/if}}
  </template>
}
